/**
 * Instagram Browser Detection and Optimization
 * Ensures all templates work perfectly in Instagram in-app browser
 */

/**
 * Detect if user is using Instagram in-app browser (Instagram Explorer).
 * Covers links opened from bio, stories, DMs, etc.
 */
export function isInstagramBrowser(): boolean {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent.toLowerCase();
  const ref =
    typeof document !== "undefined" && document.referrer
      ? document.referrer.toLowerCase()
      : "";

  // Instagram / Meta in-app WebView — UA markers (Explorer, Stories, bio, DMs)
  if (ua.includes("instagram")) return true;
  if (ua.includes("iabmv")) return true; // Instagram IABMV/1
  if (ua.includes("fb_iab") || ua.includes("fbav") || ua.includes("fban")) return true;
  if (ua.includes("fbios") || ua.includes("fb_iab")) return true;
  // Line / other Meta wrappers sometimes strip "instagram" but keep IAB
  if (ua.includes("mobile") && ua.includes("wv") && ua.includes("instagram")) return true;

  // Referrer fallback: opened from Instagram / Facebook (bio, story, link sticker)
  if (
    ua.includes("mobile") &&
    ua.includes("webkit") &&
    (ref.includes("instagram") ||
      ref.includes("facebook.com") ||
      ref.includes("l.instagram.com") ||
      ref.includes("lm.facebook.com"))
  ) {
    return true;
  }

  return false;
}

/** Alias for clarity when handling Instagram Explorer specifically. */
export const isInstagramExplorer = isInstagramBrowser;

/**
 * Detect mobile device
 */
export function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;

  const userAgent = navigator.userAgent.toLowerCase();
  return (
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
    window.innerWidth <= 768
  );
}

/**
 * Get Instagram browser optimizations
 */
export function getInstagramOptimizations() {
  const isInstagram = isInstagramBrowser();
  const isMobile = isMobileDevice();

  return {
    isInstagramBrowser: isInstagram,
    isMobile,
    
    // CSS optimizations for Instagram browser
    cssClasses: {
      // Ensure proper scrolling in Instagram browser
      container: isInstagram ? "instagram-browser-container" : "",
      
      // Better touch targets for Instagram browser
      buttons: isInstagram ? "instagram-browser-buttons" : "",
      
      // Optimized gallery for Instagram browser
      gallery: isInstagram ? "instagram-browser-gallery" : "",
      
      // Modal optimizations
      modal: isInstagram ? "instagram-browser-modal" : ""
    },
    
    // Behavioral optimizations
    behavior: {
      // Disable certain animations in Instagram browser for better performance
      disableAnimations: isInstagram,
      
      // Use different scroll behavior
      useNativeScroll: isInstagram,
      
      // Optimize video playback
      optimizeVideo: isInstagram,
      
      // Better modal handling
      improveModalHandling: isInstagram
    },
    
    // Technical info
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    }
  };
}

/**
 * Apply Instagram browser CSS optimizations
 */
export function applyInstagramBrowserCSS(): void {
  if (typeof window === "undefined") return;

  const isInstagram = isInstagramBrowser();
  if (!isInstagram) return;

  // Create style element for Instagram browser optimizations
  const styleId = "instagram-browser-optimizations";
  
  // Remove existing styles
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
  }

  // Add Instagram browser specific styles
  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    /* Instagram Browser Optimizations */
    .instagram-browser-container {
      -webkit-overflow-scrolling: touch !important;
      overflow-x: hidden !important;
      position: relative !important;
      width: 100% !important;
      height: 100% !important;
    }
    
    .instagram-browser-buttons {
      touch-action: manipulation !important;
      -webkit-tap-highlight-color: transparent !important;
      user-select: none !important;
    }
    
    .instagram-browser-gallery {
      -webkit-transform: translateZ(0) !important;
      transform: translateZ(0) !important;
      will-change: transform !important;
    }
    
    .instagram-browser-modal {
      position: fixed !important;
      -webkit-transform: translate3d(0,0,0) !important;
      transform: translate3d(0,0,0) !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      z-index: 9999 !important;
      overflow: hidden !important;
      touch-action: pan-y pinch-zoom !important;
    }
    
    /* Lightbox content in Instagram - allow vertical scroll but handle horizontal swipes */
    .instagram-browser-modal > div {
      touch-action: pan-y pinch-zoom !important;
      -webkit-overflow-scrolling: touch !important;
    }
    
    /* Prevent body scroll when lightbox is open in Instagram */
    body:has(.instagram-browser-modal) {
      overflow: hidden !important;
      position: fixed !important;
      width: 100% !important;
      height: 100% !important;
    }
    
    /* Better video handling in Instagram browser */
    .instagram-browser-container video,
    .instagram-browser-container .vjs-tech {
      -webkit-playsinline: true !important;
      playsinline: true !important;
      x5-playsinline: true !important;
      x5-video-player-type: h5 !important;
      x5-video-player-fullscreen: false !important;
      object-fit: cover !important;
      width: 100% !important;
      height: 100% !important;
    }
    
    /* Improved touch targets for Instagram */
    .instagram-browser-buttons button,
    .instagram-browser-container button {
      min-height: 44px !important;
      min-width: 44px !important;
      -webkit-tap-highlight-color: transparent !important;
      touch-action: manipulation !important;
    }
    
    /* Prevent zoom on input focus */
    .instagram-browser-container input,
    .instagram-browser-container textarea,
    .instagram-browser-container select {
      font-size: 16px !important;
    }
    
    /* Viewport height fixes for Instagram - prevent half-screen blank
       El chrome del WebView de IG aparece/desaparece y cambia el viewport en
       caliente (733→423→733 en el replay 019f8489). Reglas clave:
         - min-height NUNCA en 100vh: "vh" es el viewport LARGO, así que con la
           barra desplegada el contenedor queda más alto que lo visible y empuja
           el CTA fuera de pantalla. "100svh" (viewport CORTO) siempre entra.
         - height en 100dvh: sigue el viewport real y se reajusta solo.
       Se mantiene el fallback en cascada (svh/dvh son Safari 15.4+ / Chrome 108+;
       los navegadores viejos se quedan con la declaración previa). */
    .instagram-browser-container {
      min-height: 100vh;
      min-height: 100svh !important;
    }
    @supports (-webkit-touch-callout: none) {
      /* iOS: -webkit-fill-available ya excluye la barra, es el equivalente a svh */
      .instagram-browser-container {
        height: -webkit-fill-available !important;
        min-height: -webkit-fill-available !important;
      }
    }
    /* Fix fullscreen video container in Instagram WebView */
    .instagram-browser-modal {
      min-height: 100vh;
      min-height: 100svh !important;
      height: 100%;
      height: 100vh;
      height: 100dvh !important;
    }
    
    /* Smooth scrolling for Instagram */
    .instagram-browser-container {
      scroll-behavior: smooth !important;
      -webkit-overflow-scrolling: touch !important;
    }
    
    /* Prevent text selection issues in Instagram */
    .instagram-browser-container * {
      -webkit-user-select: none !important;
      user-select: none !important;
    }
    
    .instagram-browser-container input,
    .instagram-browser-container textarea {
      -webkit-user-select: text !important;
      user-select: text !important;
    }

    /* ── DualCTA / invitacion Mux VIP landing (unified) ───────────────────
       IG chrome steals bottom space and resizes viewport; keep video + CTA
       inside the visible area and force media surface paint. */
    [data-dual-cta-landing][data-ig-browser="1"] {
      position: fixed !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      height: 100svh !important;
      height: var(--vv-height, 100dvh) !important;
      max-height: var(--vv-height, 100dvh) !important;
      overflow: hidden !important;
      overscroll-behavior: none !important;
      -webkit-transform: translate3d(0,0,0) !important;
      transform: translate3d(0,0,0) !important;
    }
    [data-dual-cta-landing][data-ig-browser="1"] [data-dual-cta-buttons] {
      padding-bottom: max(
        2.5rem,
        calc(env(safe-area-inset-bottom, 0px) + var(--ig-chrome-inset, 0px) + 1.25rem)
      ) !important;
      z-index: 130 !important;
    }
    [data-dual-cta-landing][data-ig-browser="1"] [data-dual-cta-buttons] button {
      min-height: 56px !important;
      font-size: 15px !important;
      touch-action: manipulation !important;
      -webkit-tap-highlight-color: transparent !important;
    }
    /* Video surface must stay visible (controller was historically display:none) */
    [data-dual-cta-landing][data-ig-browser="1"] mux-player,
    .instagram-browser-modal mux-player,
    .instagram-browser-container mux-player {
      width: 100% !important;
      height: 100% !important;
      --media-object-fit: cover !important;
    }
    [data-dual-cta-landing][data-ig-browser="1"] mux-player::part(controller),
    [data-dual-cta-landing][data-ig-browser="1"] mux-player::part(media-layer),
    [data-dual-cta-landing][data-ig-browser="1"] mux-player::part(layer),
    [data-dual-cta-landing][data-ig-browser="1"] mux-player::part(video) {
      display: block !important;
      opacity: 1 !important;
      visibility: visible !important;
      width: 100% !important;
      height: 100% !important;
    }
    /* End card fully above IG bottom bar */
    [data-dual-cta-landing][data-ig-browser="1"] [data-dual-cta-end-card],
    [data-invitacion-end-card] {
      padding-bottom: max(
        2rem,
        calc(env(safe-area-inset-bottom, 0px) + var(--ig-chrome-inset, 0px) + 1rem)
      ) !important;
    }
  `;
  
  document.head.appendChild(style);
}

/**
 * Instagram / Meta WebView steals bottom chrome; track visualViewport so CTA
 * stays above the bar (resize/scroll of the IAB toolbar).
 * Returns cleanup.
 */
export function bindInstagramVisualViewport(
  root: HTMLElement | null,
  onChromeInset?: (px: number) => void,
): () => void {
  if (typeof window === "undefined" || !root || !isInstagramBrowser()) {
    return () => undefined;
  }

  const apply = () => {
    const vv = window.visualViewport;
    const height = vv?.height ?? window.innerHeight;
    const offsetTop = vv?.offsetTop ?? 0;
    const chromeInset = Math.max(
      0,
      Math.round(window.innerHeight - height - offsetTop),
    );
    root.style.setProperty("--vv-height", `${Math.round(height)}px`);
    root.style.setProperty("--ig-chrome-inset", `${chromeInset}px`);
    onChromeInset?.(chromeInset);
  };

  apply();
  const vv = window.visualViewport;
  vv?.addEventListener("resize", apply);
  vv?.addEventListener("scroll", apply);
  window.addEventListener("orientationchange", apply);
  window.addEventListener("resize", apply);

  return () => {
    vv?.removeEventListener("resize", apply);
    vv?.removeEventListener("scroll", apply);
    window.removeEventListener("orientationchange", apply);
    window.removeEventListener("resize", apply);
  };
}

/**
 * Track Instagram browser usage
 */
export function trackInstagramBrowserUsage(templateName: string, country: string): void {
  const optimizations = getInstagramOptimizations();
  
  if (optimizations.isInstagramBrowser) {
    // Track Instagram browser usage
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "instagram_browser_detected", {
        event_category: "browser_detection",
        event_label: `${templateName}_instagram_browser`,
        custom_parameter_1: country,
        custom_parameter_2: templateName,
        custom_parameter_3: "instagram_browser",
        custom_parameter_4: optimizations.isMobile ? "mobile" : "desktop"
      });
    }

  }
}

export default {
  isInstagramBrowser,
  isInstagramExplorer,
  isMobileDevice,
  getInstagramOptimizations,
  applyInstagramBrowserCSS,
  trackInstagramBrowserUsage,
};